import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebouncedValue } from "./useDebouncedValue";

/**
 * Search + filter + sort + paging state for a client-side table, kept in the URL query so a
 * filtered view can be linked, reloaded and navigated with Back/Forward.
 *
 * URL layout (all optional — a value equal to its default is never written, so a clean URL
 * means "the default view"):
 *   q=<search>  sort=<key>|-<key> (leading "-" = descending)  page=<1-based>  size=<n>
 *   <filterKey>=<value> for every key in `defaultFilters`
 *
 * `paramPrefix` namespaces all of them, for a page that hosts two tables. Params the hook
 * does not own (e.g. a page-level `tab=`) are preserved on every write.
 *
 * Writes use `replace`, so typing in the search box does not push one history entry per
 * keystroke. The search input itself is local state; only its debounced value reaches the
 * URL and the returned `query` used for filtering.
 */

export type SortDir = "asc" | "desc";
export interface SortState {
  key: string;
  dir: SortDir;
}

export interface TableStateOptions<F extends Record<string, string>> {
  defaultFilters: F;
  defaultSort: SortState;
  defaultPageSize: number;
  pageSizes: number[];
  /** Keys whose FIRST click sorts descending (numbers / timestamps read best high-first). */
  descFirst?: string[];
  debounceMs?: number;
  paramPrefix?: string;
}

export interface TableState<F extends Record<string, string>> {
  /** What the search box shows (immediate). */
  queryInput: string;
  setQueryInput: (q: string) => void;
  /** Debounced, trimmed, lower-cased search used for filtering. */
  query: string;
  filters: F;
  setFilter: <K extends keyof F>(key: K, value: F[K]) => void;
  /** Set several filters in ONE URL write (e.g. a stat chip that implies two axes). */
  setFilters: (changes: Partial<F>) => void;
  resetFilters: () => void;
  /** Number of filters (plus search) that differ from their defaults. */
  activeFilterCount: number;
  sort: SortState;
  toggleSort: (key: string) => void;
  /** 1-based, NOT clamped — clamp with `paginate`. */
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
}

export const parseSort = (raw: string | null, fallback: SortState): SortState => {
  if (!raw) return fallback;
  const desc = raw.startsWith("-");
  const key = desc ? raw.slice(1) : raw;
  return key ? { key, dir: desc ? "desc" : "asc" } : fallback;
};

export const serializeSort = (s: SortState): string => (s.dir === "desc" ? `-${s.key}` : s.key);

export function useTableState<F extends Record<string, string>>(
  options: TableStateOptions<F>
): TableState<F> {
  const {
    defaultFilters,
    defaultSort,
    defaultPageSize,
    pageSizes,
    descFirst = [],
    debounceMs = 250,
    paramPrefix = "",
  } = options;
  const [params, setParams] = useSearchParams();
  const k = useCallback((name: string) => `${paramPrefix}${name}`, [paramPrefix]);

  // Options are usually inline literals; key the memos on their content, not identity.
  const defaultsKey = JSON.stringify(defaultFilters);
  const defaultSortKey = serializeSort(defaultSort);
  const descFirstKey = descFirst.join(",");
  const pageSizesKey = pageSizes.join(",");

  /** One URL write; null / "" deletes the param. Always resets nothing implicitly. */
  const update = useCallback(
    (changes: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [name, value] of Object.entries(changes)) {
            if (value == null || value === "") next.delete(name);
            else next.set(name, value);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  const filters = useMemo(() => {
    const defaults = JSON.parse(defaultsKey) as F;
    const out = { ...defaults };
    (Object.keys(defaults) as (keyof F)[]).forEach((key) => {
      const v = params.get(k(String(key)));
      if (v != null && v !== "") out[key] = v as F[keyof F];
    });
    return out;
  }, [params, defaultsKey, k]);

  const sort = useMemo(
    () => parseSort(params.get(k("sort")), parseSort(defaultSortKey, defaultSort)),
    // defaultSort is represented by defaultSortKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params, defaultSortKey, k]
  );

  const pageRaw = Number(params.get(k("page")));
  const page = Number.isInteger(pageRaw) && pageRaw >= 1 ? pageRaw : 1;

  const sizeRaw = Number(params.get(k("size")));
  const pageSize = pageSizesKey.split(",").map(Number).includes(sizeRaw) ? sizeRaw : defaultPageSize;

  // --- search: local input, debounced into the URL ------------------------------------
  const urlQuery = params.get(k("q")) ?? "";
  const [queryInput, setQueryInput] = useState(urlQuery);
  const debounced = useDebouncedValue(queryInput, debounceMs);
  /** The last q this hook wrote — so its own write is not mistaken for a Back/Forward. */
  const lastWritten = useRef(urlQuery);

  useEffect(() => {
    if (debounced === lastWritten.current) return;
    lastWritten.current = debounced;
    update({ [k("q")]: debounced, [k("page")]: null });
  }, [debounced, update, k]);

  useEffect(() => {
    // The URL changed from outside (Back, a link): show it in the box.
    if (urlQuery === lastWritten.current) return;
    lastWritten.current = urlQuery;
    setQueryInput(urlQuery);
  }, [urlQuery]);

  const setFilters = useCallback(
    (changes: Partial<F>) => {
      const defaults = JSON.parse(defaultsKey) as F;
      const writes: Record<string, string | null> = { [k("page")]: null };
      (Object.keys(changes) as (keyof F)[]).forEach((key) => {
        const v = changes[key] as string;
        writes[k(String(key))] = v === defaults[key] ? null : v;
      });
      update(writes);
    },
    [defaultsKey, k, update]
  );

  const setFilter = useCallback(
    <K extends keyof F>(key: K, value: F[K]) => setFilters({ [key]: value } as unknown as Partial<F>),
    [setFilters]
  );

  const resetFilters = useCallback(() => {
    const defaults = JSON.parse(defaultsKey) as F;
    const writes: Record<string, string | null> = { [k("page")]: null, [k("q")]: null };
    Object.keys(defaults).forEach((key) => {
      writes[k(key)] = null;
    });
    lastWritten.current = "";
    setQueryInput("");
    update(writes);
  }, [defaultsKey, k, update]);

  const toggleSort = useCallback(
    (key: string) => {
      let next: SortState;
      if (sort.key === key) next = { key, dir: sort.dir === "asc" ? "desc" : "asc" };
      else next = { key, dir: descFirstKey.split(",").includes(key) ? "desc" : "asc" };
      const s = serializeSort(next);
      update({ [k("sort")]: s === defaultSortKey ? null : s, [k("page")]: null });
    },
    [sort, descFirstKey, defaultSortKey, k, update]
  );

  const setPage = useCallback(
    (p: number) => update({ [k("page")]: p <= 1 ? null : String(p) }),
    [k, update]
  );

  const setPageSize = useCallback(
    (size: number) =>
      update({ [k("size")]: size === defaultPageSize ? null : String(size), [k("page")]: null }),
    [defaultPageSize, k, update]
  );

  const activeFilterCount = useMemo(() => {
    const defaults = JSON.parse(defaultsKey) as F;
    let n = (Object.keys(defaults) as (keyof F)[]).filter((key) => filters[key] !== defaults[key])
      .length;
    if (urlQuery.trim()) n++;
    return n;
  }, [filters, defaultsKey, urlQuery]);

  return {
    queryInput,
    setQueryInput,
    // From the URL (which the debounce writes), so a reset / Back applies immediately.
    query: urlQuery.trim().toLowerCase(),
    filters,
    setFilter,
    setFilters,
    resetFilters,
    activeFilterCount,
    sort,
    toggleSort,
    page,
    setPage,
    pageSize,
    setPageSize,
  };
}

// --- pure helpers (no React) — exported for tests and for non-hook callers -------------

export type SortValue = string | number | null | undefined;
export type SortAccessors<T> = Record<string, (row: T) => SortValue>;

/**
 * Compare two sort values. Empty values (null / undefined / NaN / "") sort LAST in both
 * directions — a "—" cell at the top of a descending wind sort is never what anyone wants.
 */
export const compareValues = (a: SortValue, b: SortValue, dir: SortDir): number => {
  const emptyA = a == null || a === "" || (typeof a === "number" && Number.isNaN(a));
  const emptyB = b == null || b === "" || (typeof b === "number" && Number.isNaN(b));
  if (emptyA && emptyB) return 0;
  if (emptyA) return 1;
  if (emptyB) return -1;
  const sign = dir === "asc" ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return (a - b) * sign;
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true }) * sign;
};

/** Stable sort by `sort.key`; unknown keys leave the order untouched. */
export function sortRows<T>(
  rows: T[],
  sort: SortState,
  accessors: SortAccessors<T>,
  tiebreak?: (a: T, b: T) => number
): T[] {
  const get = accessors[sort.key];
  if (!get) return rows;
  return rows
    .map((row, i) => ({ row, i }))
    .sort((x, y) => {
      const c = compareValues(get(x.row), get(y.row), sort.dir);
      if (c !== 0) return c;
      const t = tiebreak ? tiebreak(x.row, y.row) : 0;
      return t !== 0 ? t : x.i - y.i;
    })
    .map((x) => x.row);
}

/** Slice one page; the requested page is clamped into range. */
export function paginate<T>(
  rows: T[],
  page: number,
  pageSize: number
): { pageRows: T[]; page: number; totalPages: number; total: number } {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const p = Math.min(Math.max(1, page), totalPages);
  return {
    pageRows: rows.slice((p - 1) * pageSize, p * pageSize),
    page: p,
    totalPages,
    total: rows.length,
  };
}
